import '@js-joda/timezone';

import {
  ChronoUnit,
  DateTimeFormatter,
  Duration,
  LocalDate,
  TemporalAccessor,
  ZonedDateTime,
  ZoneId,
} from '@js-joda/core';
import { Locale } from '@js-joda/locale_en-us';
import pluralize from 'pluralize';

const importDateFormat = DateTimeFormatter.ofPattern(
  "MMM d, yyyy 'at' h:mm a z",
).withLocale(Locale.US);

const dayOrdinals: Record<number, string> = {};
for (let i = 1; i <= 31; i += 1) {
  dayOrdinals[i] = i.toString();
}

export const parseZdt = (
  date: string | null | undefined,
): ZonedDateTime | null => (date ? ZonedDateTime.parse(date) : null);

export const getDurationSince = (
  from: ZonedDateTime | null | undefined,
  to: ZonedDateTime = ZonedDateTime.now(),
) => {
  if (!from) {
    return null;
  }
  const duration = Duration.between(from, to);
  const hours = duration.toHours();
  const days = duration.toDays();
  const weeks = duration.toDays() / 7;

  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''} ago`;
  }
  if (weeks > 0) {
    return `${weeks} weeks ago`;
  }
  if (hours > 0) {
    return `${hours} hours ago`;
  }
  if (duration.toMinutes() > 0) {
    return `${duration.toMinutes()} minutes ago`;
  }

  return `${duration.toHours()} hours ${duration.toMinutes()} minutes`;
};

const applyDateFormat =
  (fmt: DateTimeFormatter) => (date: string | TemporalAccessor) => {
    const temporal =
      typeof date === 'string' ? ZonedDateTime.parse(date) : date;
    return fmt.format(temporal);
  };

const shortDateFormat = DateTimeFormatter.ofPattern('MM/dd/yyyy').withLocale(
  Locale.US,
);

export const shortDate = applyDateFormat(shortDateFormat);

/**
 * F O R M A T T E R S
 */

export const iso8601DateTime = applyDateFormat(DateTimeFormatter.ISO_INSTANT);

export const dateAndTime = applyDateFormat(
  DateTimeFormatter.ofPattern('MMM d, yyyy, h:mm a').withLocale(Locale.US),
);

export const formatImportDate = (
  date: string | null | undefined,
  zoneId: ZoneId = ZoneId.systemDefault(),
): string[] => {
  if (!date) {
    return ['Never'];
  }
  const zdt = ZonedDateTime.parse(date).withZoneSameInstant(zoneId);
  return [importDateFormat.format(zdt)];
};

// Oct 22, 2001
const monthDayYearFormat = DateTimeFormatter.ofPattern('MMM d, y').withLocale(
  Locale.US,
);

export const monthDayYear = applyDateFormat(monthDayYearFormat);

/**
 * T I M E Z O N E   S H E N A N I G A N S
 *
 * ZonedDateTime.now()                  = "2021-05-14T12:23:56.586-04:00[America/New_York]"
 * .withZoneSameInstant(ZoneId.SYSTEM)  = "2021-05-14T12:23:56.586-04:00[SYSTEM]"
 */

export const getTimeSince = (
  date?: LocalDate,
  today: LocalDate = LocalDate.now(),
): string => {
  const unknown = 'unknown';
  try {
    if (!date) {
      return unknown;
    }

    const years = date.until(today, ChronoUnit.YEARS);
    const months = date.until(today, ChronoUnit.MONTHS);
    const weeks = date.until(today, ChronoUnit.WEEKS);
    const days = date.until(today, ChronoUnit.DAYS);

    if (years > 0) {
      return `${pluralize('year', years, true)} ago`;
    } else if (months > 0) {
      return `${pluralize('month', months, true)} ago`;
    } else if (weeks > 0) {
      return `${pluralize('week', weeks, true)} ago`;
    } else if (days === 0) {
      return 'Today';
    } else if (days === 1) {
      return 'Yesterday';
    } else if (days > 0) {
      return `${days} days ago`;
    } else if (days === -1) {
      return 'Tomorrow';
    } else if (days < -1) {
      return date.format(monthDayYearFormat);
    } else {
      return unknown;
    }
  } catch {
    return unknown;
  }
};
